import React from "react";
import styled from "styled-components";
import { Icon } from "@material-ui/core";
import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import { noop } from "lodash-es";
import { app } from "js/namespaces";
import { UIPane, RoundIconButton } from "js/react/components/UiComponents";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import { Breadcrumb } from "js/editor/PresentationLibrary/PresentationLibrary";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    UIPaneResultsContainer,
    InnerWhiteFrameContainer,
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { SortMenu } from "js/react/views/AddSlide/Panes/Components/SortMenu";

import Update from "mdi-material-ui/Update";
import CalendarRange from "mdi-material-ui/CalendarRange";

import Loadable from "js/react/components/Loadable";
import ImageThumbnailGrid from "js/react/views/AddAssets/Components/ImageThumbnailGrid";
import AssetPreview from "js/react/views/AddAssets/Components/AssetPreview";
import { ds } from "js/core/models/dataService";
import Api from "js/core/api";
import { ASSET_RESOLUTION, AssetType, PERMISSION_RESOURCE_TYPE } from "common/constants";
import { UIController } from "js/editor/dialogs/UIController";

import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { trackActivity } from "js/core/utilities/utilities";
import { NoMatchNotice, NoTeamAssetNotice } from "js/react/components/Notice";

const TEAM_ASSET = PERMISSION_RESOURCE_TYPE.TEAM_ASSET;

const StyledUIPaneResultsContainer = styled(UIPaneResultsContainer)`
  ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarContainer = styled(SearchBarContainer)`
    background: #eee;
    ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarInnerContainer = styled(SearchBarInnerContainer)`
    background: ${props => props.editable ? "white" : "#eee"};
`;

const filterOptions = [
    {
        id: "all",
        name: "All Media"
    },
    {
        id: "image",
        name: "Photos"
    },
    // {
    //     id: "stock_video",
    //     name: "Videos"
    // },
    // {
    //     id: "icon",
    //     name: "Icons"
    // },
    {
        id: "logo",
        name: "Logos"
    },
];

export class TeamAssetsPane extends React.Component {
    state = {
        loadedAssets: {},
        isLoading: false,
        muted: true,
        mediaTypeFilter: "all",
        sortBy: "createdAt",
    };

    lookupPreviewRefs = {};

    componentDidMount = async () => {
        const { prefilledSearchQuery } = this.props;
        const prefill = prefilledSearchQuery;
        await this.doSearch(prefill ?? "");

        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        team?.on("change:sharedResources", this.onChangeSharedResources);
    };

    onChangeSharedResources = model => {
        this.setState({ loadedAssets: {} }, () => {
            this.getAssetsToLoad().then(assets => {
                this.setState({
                    loadedAssets: assets,
                });
            });
        });
    }

    componentWillUnmount() {
        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        team?.off("change:sharedResources", this.onChangeSharedResources);
    }

    doSearch = async searchTerm => {
        this.setState({
            isLoading: true,
            isSearching: true,
            searchResults: []
        });

        const assets = await this.getAssetsToLoad();

        this.setState({
            loadedAssets: assets,
            isLoading: false,
            isSearching: false
        });
    };

    handleSearch = async searchTerm => {
        await this.doSearch(searchTerm);
    };

    async loadAsset(asset) {
        try {
            const previewProps = { ...asset.attributes };

            if (asset.type === AssetType.VIDEO || asset.type === AssetType.STOCK_VIDEO) {
                if (asset.type === AssetType.VIDEO) {
                    previewProps.url = await asset.getURL();
                    const previewAssetId = asset.get("previewAssetId");
                    if (previewAssetId) {
                        const previewAsset = await ds.assets.getAssetById(previewAssetId, AssetType.IMAGE);
                        previewProps.previewUrl = await previewAsset.getURL();
                    }
                }
            } else {
                const url = await asset.getURL(ASSET_RESOLUTION.SMALL);
                previewProps.url = url;
            }

            // hash for team assets, if any
            previewProps.hash = asset.get("hash");

            // Ensure we have width and height values
            previewProps.width = previewProps.width || previewProps.w;
            previewProps.height = previewProps.height || previewProps.h;

            return { asset, previewProps };
        } catch (error) {
            console.error(error);
            return { asset, error };
        }
    }

    async getAssetsToLoad(teamAssets) {
        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        teamAssets = teamAssets ?? { ...(team?.get("sharedResources") ?? {})[TEAM_ASSET] };

        const assetIds = Object.keys(teamAssets);
        const type = AssetType.IMAGE; // HACK
        const assets = await Promise.all(assetIds.map(id => ds.assets.getAssetById(id, type)));

        // Load urls now
        const latestAssets = {
            ...this.state.loadedAssets,
            ...Object.fromEntries(await Promise.all(assets.map(async asset => ([asset.id, await this.loadAsset(asset)])))),
        };

        return latestAssets;
    }

    handleAddItem = async () => {
        const overwriteAssetId = UIController.getOrganizationId() + ":" + uuid();
        ShowDialog(AddAssetsContainer, {
            assetType: AssetType.IMAGE,
            hideTeamAssets: true,
            hideIcons: true,
            hideVideos: true,
            overwriteAssetId,
            callback: async (model, isNew) => {
                const workspace = app.user.workspaces[app.appState.workspaceId];
                const team = workspace.getDefaultTeam();

                if (team) {
                    let assetId = model.content_value;

                    let srcAsset = await ds.assets.getAssetById(assetId);
                    let assetValues = srcAsset.attributes;

                    if (!isNew) {
                        // add an existing asset to the team - we need to copy it into the new ID
                        assetId = overwriteAssetId;
                        assetValues = {
                            ...assetValues,
                            id: assetId,
                            orgId: UIController.getOrganizationId(),
                            hash: model.content_value,
                            userId: null,
                            actualId: model.content_value,
                            createdAt: new Date().getTime(),
                        };
                        await Api.assets.put(assetValues);
                    }

                    // add to team's sharedResources
                    team.update({ "sharedResources": { [TEAM_ASSET]: { [assetId]: true } } });

                    trackActivity("TeamAsset", "Added", null, null, {
                        id: assetValues.id,
                        orgId: assetValues.orgId,
                        type: assetValues.type,
                        isNew,
                    });

                    const teamAssets = { ...(team.get("sharedResources") ?? {})[TEAM_ASSET] };
                    teamAssets[assetId] = true;
                    this.getAssetsToLoad(teamAssets).then(assets => {
                        this.setState({
                            loadedAssets: assets
                        });
                    });
                }
            }
        });
    }

    handleReplaceItem = async asset => {
        const showAssetsDialog = () => {
            ShowDialog(AddAssetsContainer, {
                assetType: asset.type,
                hideTeamAssets: true,
                hideIcons: true,
                hideVideos: true,
                hidePhotos: asset.type !== AssetType.IMAGE,
                hideLogos: asset.type !== AssetType.LOGO,
                overwriteAssetId: asset.id,
                callback: async (model, isNew) => {
                    let srcAsset = await ds.assets.getAssetById(model.content_value);
                    let assetValues = srcAsset.attributes;

                    if (!isNew) {
                        // replace an existing asset with another existing one - we need to copy it over the existing ID
                        assetValues = {
                            ...assetValues,
                            orgId: UIController.getOrganizationId(),
                            hash: model.content_value,
                            userId: null,
                            actualId: model.content_value
                        };
                        asset.update(assetValues, {
                            replaceKeys: true,
                        });
                        await ds.assets.updatePromise;
                    }

                    trackActivity("TeamAsset", "Replaced", null, null, {
                        id: assetValues.id,
                        orgId: assetValues.orgId,
                        type: assetValues.type,
                        isNew,
                    });

                    // re-load and re-render
                    this.loadAsset(asset).then(result => {
                        this.setState({
                            loadedAssets: {
                                ...this.state.loadedAssets,
                                [asset.id]: result
                            }
                        });

                        this.forceUpdate();
                    });
                }
            });
        };

        ShowConfirmationDialog({
            title: "Continue changing this shared team asset?",
            message: "Changes will be applied to wherever this asset is used.",
            acceptCallback: showAssetsDialog,
            cancelCallback: noop,
            okButtonLabel: "Continue"
        });
    }

    handleDeleteItem = async asset => {
        if (await ShowConfirmationDialog({
            title: "Are you sure you want to delete this asset?",
            message: "This action can not be undone.",
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: "Delete",
        })) {
            const workspace = app.user.workspaces[app.appState.workspaceId];
            const team = workspace.getDefaultTeam();

            if (team) {
                // remove entry from team's sharedResources
                team.update({ "sharedResources": { [TEAM_ASSET]: { [asset.id]: null } } });

                let assetValues = asset.attributes;

                trackActivity("TeamAsset", "Deleted", null, null, {
                    id: assetValues.id,
                    orgId: assetValues.orgId,
                    type: assetValues.type,
                });

                // update ui
                const loadedAssets = { ...this.state.loadedAssets };
                delete loadedAssets[asset.id];
                this.setState({ loadedAssets });
            }
        }
    }

    handleHideItem = async asset => {
        // flag as hidden
        asset.update({ hidden: true });
        this.forceUpdate();

        let assetValues = asset.attributes;

        trackActivity("TeamAsset", "Hidden", null, null, {
            id: assetValues.id,
            orgId: assetValues.orgId,
            type: assetValues.type,
        });
    }

    handleShowItem = async asset => {
        // flag as not hidden
        asset.update({ hidden: false });
        this.forceUpdate();

        let assetValues = asset.attributes;

        trackActivity("TeamAsset", "Shown", null, null, {
            id: assetValues.id,
            orgId: assetValues.orgId,
            type: assetValues.type,
        });
    }

    handleMediaFilterChange = event => {
        const value = event.target.value;
        this.setState({ mediaTypeFilter: value });
    }

    handleSort = value => {
        this.setState({ sortBy: value.field });
    }

    sortedFilteredAssets() {
        const { sortBy, loadedAssets, mediaTypeFilter } = this.state;
        const { editable } = this.props;

        // filtering
        const assetEntries = Object.entries(loadedAssets).filter(([, entry]) => {
            if (!editable && entry.asset.get("hidden")) {
                return false;
            } else if (mediaTypeFilter === "all") {
                return true;
            } else {
                return entry.asset.get("type") === mediaTypeFilter;
            }
        });

        // sorting
        assetEntries.sort(([, a], [, b]) => {
            const valueA = a.asset.get(sortBy);
            const valueB = b.asset.get(sortBy);
            return valueB - valueA;
        });

        return Object.fromEntries(assetEntries);
    }

    render() {
        const { editable, addAssetCallback, handleConfirm } = this.props;
        const { mediaTypeFilter } = this.state;
        const query = "";
        const tags = [{ id: "all_tags", name: "All Tags" }];
        const selectedTag = "all_tags";

        let items = Object.values(this.sortedFilteredAssets());

        return (
            <UIPane>
                <StyledSearchBarContainer
                    className="search-container"
                    editable={editable}
                >
                    {editable &&
                        <>
                            <Breadcrumb>Team Assets</Breadcrumb>
                            <RoundIconButton onClick={this.handleAddItem}><Icon>add</Icon></RoundIconButton>
                            {/* <Gap size={60}/> */}
                        </>
                    }
                    <div style={{ margin: "auto", flexGrow: 1000 }} />
                    <StyledSearchBarInnerContainer editable={editable}>
                        {/* <SlideSearchInput
                            query={this.global.searchSlidesQuery ||  query}
                            onSearch={this.handleSearch}
                            placeholder="Search team assets..."
                            searchOnEnter
                        /> */}

                        <FilterDropDown
                            onChange={this.handleMediaFilterChange}
                            selectedFilter={mediaTypeFilter}
                            filterOptions={filterOptions}
                        />
                        {/* <FilterDropDown
                            onChange={this.handleSelectTag}
                            selectedFilter={selectedTag}
                            filterOptions={tags}
                        /> */}
                        <SortMenu
                            disabled={query.length > 0}
                            sortOptions={this.sortOptions()}
                            onSetSort={this.handleSort}
                        />
                    </StyledSearchBarInnerContainer>
                </StyledSearchBarContainer>
                <StyledUIPaneResultsContainer editable={editable}>
                    <Loadable isLoading={this.state.isLoading}>
                        {
                            !!items.length &&
                            <InnerWhiteFrameContainer>
                                <ImageThumbnailGrid
                                    columns={3}
                                >
                                    {
                                        items.map(({
                                            asset,
                                            previewProps,
                                        }) => {
                                            // Get the ref, or create it if it doesn't exist
                                            const key = asset.id;
                                            let ref = this.lookupPreviewRefs[key];
                                            if (!ref) {
                                                ref = React.createRef();
                                                this.lookupPreviewRefs[key] = ref;
                                            }
                                            return (
                                                <AssetPreview
                                                    ref={ref}
                                                    key={key}
                                                    className={`recent-${asset.type}`}
                                                    asset={asset}
                                                    previewProps={previewProps}
                                                    assetType={asset.type}
                                                    addAssetCallback={addAssetCallback ?? (() => {})}
                                                    handleConfirm={handleConfirm ?? (() => {})}
                                                    showContextMenu
                                                    muted={false}
                                                    hidePlaybacks={() => {}}
                                                    backgroundVideoOnly={false}
                                                    menuItems={[
                                                        ...(editable ? [
                                                            {
                                                                label: "Replace With...",
                                                                onClick: () => this.handleReplaceItem(asset)
                                                            },
                                                            asset.get("hidden")
                                                                ? {
                                                                    label: "Show",
                                                                    onClick: () => this.handleShowItem(asset)
                                                                }
                                                                : {
                                                                    label: "Hide",
                                                                    onClick: () => this.handleHideItem(asset)
                                                                },
                                                            {
                                                                label: "Delete",
                                                                onClick: () => this.handleDeleteItem(asset)
                                                            }
                                                        ] : [])
                                                    ]}
                                                />
                                            );
                                        })
                                    }
                                </ImageThumbnailGrid>
                            </InnerWhiteFrameContainer>
                        }
                        {
                            !items.length &&
                            <>
                                {
                                    mediaTypeFilter === "all" &&
                                    <NoTeamAssetNotice />
                                }
                                {
                                    mediaTypeFilter !== "all" &&
                                    <NoMatchNotice />
                                }
                            </>
                        }
                    </Loadable>
                </StyledUIPaneResultsContainer>
            </UIPane>
        );
    }

    sortOptions() {
        return [/*{
            value: "name", label: "Name", icon: <SortAlphabeticalVariant />
        }, {
            value: "ratedByCount", label: "Rating", reverse: true, icon: <Star />
        }, {
            value: "presentationCount", label: "Popularity", reverse: true, icon: <Numeric />
        },*/ {
                value: "createdAt", label: "Created At", reverse: true, icon: <CalendarRange />
            }, {
                value: "modifiedAt", label: "Modified At", reverse: true, icon: <Update />
            }
        ];
    }
}
